import { IFsNodeSchema, NodeTypes } from '@cloudike/web_files'
import {
  createAsyncThunk,
  createEntityAdapter,
  createSlice,
  PayloadAction,
} from "@reduxjs/toolkit"
import {
  removeBoardFromSearchResults,
  searchActions,
  updateBoardInSearchResults
} from 'features/common/model/slice/searchSlice'
import {
  NOTIFICATION_TYPES,
  showNotification,
} from "features/common/notifications"
import { fetchAllBoardsAndTrashedNodesThunk } from "features/recently-deleted/model/slice/recentlyDeletedSlice"
import { MemberRole } from "features/team/model/types/members"
import { boardsApi } from "shared/api/boardsApi"
import { RootState } from "shared/config/store"
import { LOADING_STATUSES } from "shared/constants/loadingStatuses"
import { ROUTES } from "shared/constants/routesPaths"
import { getFilesSdk } from "shared/sdk/files"
import { hasAccess } from "shared/utils/access"
import {
  selectAllFileListItemsReducer,
  selectFileListItemReducer,
  selectFileListItemWithPressedCtrlReducer,
  selectFileListItemWithPressedShiftReducer,
  unselectAllFileListItemsReducer,
} from "shared/utils/filesListSelection"
import { getHighestLevelErrorByFieldName } from "shared/utils/filesUtils"
import { assetsActions } from 'features/assets/model/slice/assetsSlice'
import { BoardToolbarAction } from 'shared/constants/toolbar'

import { BoardsState } from "../types/boards"
import { getOrderBy } from '../../../common/utils/itemsSort'

const adapter = createEntityAdapter<IFsNodeSchema>()

export const getAvailableToolbarActionsByUserRole = (userRole: MemberRole, isDownloadEnabled: boolean, isEditContentEnabled: boolean, isPublicLink: boolean) => {
  return [
    isDownloadEnabled && {
      id: BoardToolbarAction.Download,
      name: "Download",
    },
    hasAccess(userRole, [MemberRole.ADMIN, MemberRole.OWNER, MemberRole.USER]) && isEditContentEnabled && {
      id: BoardToolbarAction.Rename,
      name: "Rename",
    },
    hasAccess(userRole, [MemberRole.ADMIN, MemberRole.OWNER, MemberRole.USER]) && isEditContentEnabled && {
      id: BoardToolbarAction.Copy,
      name: "Copy",
    },
    hasAccess(userRole, [MemberRole.ADMIN, MemberRole.OWNER, MemberRole.USER]) && isEditContentEnabled && {
      id: BoardToolbarAction.Move,
      name: "Move",
    },
    hasAccess(userRole, [MemberRole.ADMIN, MemberRole.OWNER]) && isEditContentEnabled && {
      id: BoardToolbarAction.RemoveFromBoard,
      name: "Remove from board",
    },
    hasAccess(userRole, [MemberRole.ADMIN, MemberRole.OWNER]) && isEditContentEnabled && {
      id: BoardToolbarAction.Delete,
      name: "Delete from library",
    },
    hasAccess(userRole, [MemberRole.ADMIN, MemberRole.OWNER, MemberRole.USER]) && !isPublicLink && {
      id: BoardToolbarAction.Share,
      name: "Share",
    },
    hasAccess(userRole, [MemberRole.ADMIN, MemberRole.OWNER, MemberRole.USER]) && isEditContentEnabled && {
      id: BoardToolbarAction.AddToAnotherBoard,
      name: "Add to board",
    },
  ].filter(Boolean)
}

export const boardsSelectors = adapter.getSelectors()

const initialState: BoardsState = {
  status: LOADING_STATUSES.LOADING,
  error: null,
  selectedItemsIds: [],
  lastSelectedIndexes: [],
  lastSelectedWithShiftIndex: null,
  currentBoardId: "",
  currentBoard: null,
  renamingItemId: null,
  allBoards: [],
  selectedParentId: null,
  boardFields: null,
  systemFields: null,
  systemFieldsValues: {
    statusOptions: [],
    tags: [],
  },
  boardFieldsValues: {
    status: null,
    tags: [],
    description: '',
  }
}

export const fetchAllBoardsThunk = createAsyncThunk(
  "boards/fetchAllBoardsThunk",
  async (_, { getState, dispatch }) => {
    const state = getState() as RootState
    const selectedSort = state.app.selectedSort
    const sortOrder = state.app.sortOrder
    const sdk = getFilesSdk()
    const { boardsService } = sdk
    const nodesOptions = { embedded: true, preview_jwt: true, preview: true, type__in: ['dir'], trash_state: 'live' }
    const delay = 100
    const pageSize = 500

    if(selectedSort && sortOrder) {
      nodesOptions.order_by =  getOrderBy(selectedSort, sortOrder)
    }

    const allNodes = await boardsService.getAllBoards(
      nodesOptions,
      pageSize,
      delay
    )

    dispatch(actions.setAll(allNodes))
  }
)

export const fetchAllBoardsPublicLinkThunk = createAsyncThunk(
  "boards/fetchAllBoardsPublicLinkThunk",
  async (_, { dispatch, getState }) => {
    const state = getState() as RootState
    const userID = state.user?.user?.id
    const token = state.user?.token
    const nodesOptions = { embedded: true, preview_jwt: true, preview: true, type__in: ['dir'], trash_state: 'live' }
    const sharedId = state.publicLinkFiles.nodeData?.id ?? ''
    const allNodes = await boardsApi.getBoardsPublicLink(token ? userID : 0, sharedId, nodesOptions)

    dispatch(actions.setAll(allNodes._embedded?.nodes ?? []))
  }
)

export const deleteBoardsThunk = createAsyncThunk(
  "boards/deleteBoardsThunk",
  async ({ ids, needNotify = true }: { ids: string[], needNotify?: boolean }, { dispatch }) => {
    const location = window.location.pathname
    try {
      if(!ids.length) return
      const sdk = getFilesSdk()
      const { familyFolderOperationsService: folderOperationsService } = sdk
      if (ids.length <= 100) {
        await folderOperationsService.moveNodesToTrash(ids)
      } else {
        const delay = ids.length * 5
        await folderOperationsService.batchMovingNodesToTrash(
          ids,
          undefined,
          delay
        )
      }
      dispatch(actions.unselectAll())
      dispatch(actions.updateManyItems(ids.map(id => ({ id, is_trashed: true, is_explicitly_trashed: true }))))
      dispatch(removeBoardFromSearchResults(ids))
      dispatch(searchActions.unselectAll())

      if(needNotify) {
        showNotification({
          type: NOTIFICATION_TYPES.SUCCESS,
          title: `${ids.length} ${ids.length === 1 ? 'item' : 'items'} deleted`,
          message: 'Items deleted successfully',
        })
      }

      if (location === ROUTES.dashboard.recentlyDeleted) {
        dispatch(fetchAllBoardsAndTrashedNodesThunk())
      }
    } catch (_) {
      showNotification({
        type: NOTIFICATION_TYPES.WARNING,
        title: 'Something went wrong',
        message: 'Please try again',
      })
    }
  }
)

export const downloadBoardsThunk = createAsyncThunk(
  "boards/downloadBoardsThunk",
  async ({ ids }: { ids: string[] }, { dispatch, getState }) => {
    const state = getState() as RootState
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const nodes = boardsSelectors.selectAll(state.boards)

    try {
      const sdk = getFilesSdk()
      const { familyFolderOperationsService: folderOperationsService } = sdk
      const link = await folderOperationsService.getZipStreamLink(ids)

      window.location.href = link
    } catch (error) {
      const details = getHighestLevelErrorByFieldName(error, "details")

      if (details?.node_ids === "Longer than maximum length 100") {
        showNotification({
          type: NOTIFICATION_TYPES.WARNING,
          title: 'Something went wrong',
          message: 'Please try again',
        })

        return
      }

      showNotification({
        type: NOTIFICATION_TYPES.WARNING,
        title: 'Something went wrong',
        message: 'Please try again',
      })
    } finally {
      dispatch(actions.unselectAll())
    }
  }
)

export const createNewBoardThunk = createAsyncThunk(
  "files/createFolderThunk",
  async (
    { name, parentId }: { name: string; parentId: string },
    { dispatch }
  ) => {
    try {
      const sdk = getFilesSdk()
      const { boardsService } = sdk
      const result = await boardsService.createNewBoard(
        {
          name,
          parent_id:parentId,
          unique_name: true
        }
      )

      dispatch(actions.addItem(result.data))

      showNotification({
        type: NOTIFICATION_TYPES.SUCCESS,
        title: "Create board",
        message: "Board was created",
      })
    } catch (_) {
      showNotification({
        type: NOTIFICATION_TYPES.WARNING,
        title: "Something went wrong",
        message: "Please try again",
      })
    }
  }
)

export const renameBoardThunk = createAsyncThunk(
  "boards/renameBoardThunk",
  async ({ name, boardId }: { name: string, boardId: string }, { getState, dispatch }) => {
    const state = getState() as RootState

    const boards = boardsSelectors.selectAll(state.boards)

    const board = boards.find((board) => board.id === boardId)

    const finalName = name.trim()

    if (!finalName) {
      dispatch(actions.setRenamingItemId(null))
      dispatch(actions.unselectAll())

      return
    }

    if (finalName.length > 255) {
      showNotification({
        type: NOTIFICATION_TYPES.WARNING,
        title: 'File name is too long',
        message: 'Please try again',
      })

      dispatch(actions.setRenamingItemId(null))
      dispatch(actions.unselectAll())

      return
    }

    if (name === board?.name) {
      dispatch(actions.setRenamingItemId(null))
      dispatch(actions.unselectAll())

      return
    }

    try {
      const sdk = getFilesSdk()
      const { familyFolderOperationsService: folderOperationsService } = sdk
      await folderOperationsService.renameNode(boardId as string, finalName)

      dispatch(actions.updateItem({ id: boardId, name: finalName }))
      dispatch(updateBoardInSearchResults({ id: boardId, name: finalName }))

      showNotification({
        type: NOTIFICATION_TYPES.SUCCESS,
        title: 'Board renamed',
        message: 'Board renamed successfully',
      })
    } catch (_) {
      showNotification({
        type: NOTIFICATION_TYPES.WARNING,
        title: 'Something went wrong',
        message: 'Please try again',
      })
    } finally {
      dispatch(actions.setRenamingItemId(null))
      dispatch(actions.unselectAll())
    }
  }
)


export const moveBoardsThunk = createAsyncThunk(
  "boards/moveBoardsThunk",
  async ({ ids, destinationId }: { ids: string[], destinationId: string }, { getState, dispatch }) => {
    const sdk = getFilesSdk()
    const { familyFolderOperationsService: folderOperationsService } = sdk
    const state = getState() as RootState
    const boards = boardsSelectors.selectAll(state.boards).filter((board) => ids.includes(board.id))
    try {
      for (const board of boards) {
        await folderOperationsService.moveNode(board.id, destinationId, { name: board.name })
        dispatch(actions.updateItem({ id: board.id, parent_id: destinationId }))
      }
      showNotification({
        type: NOTIFICATION_TYPES.SUCCESS,
        title: `${ids.length} ${ids.length === 1 ? 'item' : 'items'} moved`,
        message: 'Items moved successfully',
      })
    } catch (_) {
      showNotification({
        type: NOTIFICATION_TYPES.WARNING,
        title: 'Something went wrong',
        message: 'Please try again',
      })
      throw new Error('Something went wrong with moving boards')
    }
  }
)

export const copyBoardsThunk = createAsyncThunk(
  "boards/copyBoardsThunk",
  async ({ ids, destinationId }: { ids: string[], destinationId: string }, { getState, dispatch }) => {
    const sdk = getFilesSdk()
    const { familyFolderOperationsService: folderOperationsService } = sdk
    const state = getState() as RootState
    const boards = boardsSelectors.selectAll(state.boards).filter((board) => ids.includes(board.id))
    try {
      for (const board of boards) {
        const source = board?._links?.self?.href
        const params = {
          source: source,
          destination: {
            parent_id: destinationId
          }
        }
        const { data:{ destination:{ id } } } = await folderOperationsService.copyDir(params)
        dispatch(actions.addItem({ ...board, id, parent_id: destinationId }))
      }
      showNotification({
        type: NOTIFICATION_TYPES.SUCCESS,
        title: `${ids.length} ${ids.length === 1 ? 'item' : 'items'} copied`,
        message: 'Items copied successfully',
      })
    } catch (_) {
      showNotification({
        type: NOTIFICATION_TYPES.WARNING,
        title: 'Something went wrong',
        message: 'Please try again',
      })
      throw new Error('Something went wrong with copying boards')
    }
  }
)

export const fetchSystemFieldsThunk = createAsyncThunk(
  "boards/fetchSystemTagsThunk",
  async function ({ signal }:{signal:AbortSignal}, { getState, dispatch }):Promise<any> {
    const state = getState() as RootState
    const user = state.user.user
    const userID = user?.family_user_id || user?.user_id
    const rsp = await boardsApi.getSystemFields(userID, signal)
    const { _embedded:{ fields } } = rsp
    const tagsFieldObj = fields.find((field) => field?.title === 'tags')
    const statusFieldObj = fields.find((field) => field?.title === 'status')

    const { options: tagsFieldValue } = tagsFieldObj
    const { options: statusOptions }  = statusFieldObj
    dispatch(actions.setSystemFields(fields))
    dispatch(actions.setSystemFieldsValues({ tags: tagsFieldValue, statusOptions: statusOptions }))
  }
)

export const fetchBoardDataThunk = createAsyncThunk(
  'boards/fetchBoardDataThunk',
  async function({ nodeId, signal }: { nodeId: string, signal?: AbortSignal }, { dispatch, getState }) {
    const state = getState() as RootState
    const userID = state.user.user.family_user_id

    const boardFields = await boardsApi.getBoardFields(userID, nodeId, signal)

    const embeddedFields = boardFields?._embedded?.fields ?? []
    const statusFieldObj = embeddedFields.find((field) => field?.field?.title === 'status')
    const tagsFieldObj = embeddedFields.find((field) => field?.field?.title === 'tags')
    const descriptionFieldObj = embeddedFields.find((field) => field?.field?.title === 'description')

    const statusFieldValue = statusFieldObj?.value ?? null
    const tagsFieldValue = tagsFieldObj?.value ?? []
    const descriptionFieldValue = descriptionFieldObj?.value ?? ''

    dispatch(actions.setBoardFieldsValues({
      status: statusFieldValue,
      tags: tagsFieldValue,
      description: descriptionFieldValue,
    }))
  }
)

export const updateSystemFieldThunk = createAsyncThunk(
  'assets/updateSystemFieldThunk',
  async ({ fieldID, data, signal }: { fieldID: string, data: any, signal: any }, { getState }) => {
    const state = getState() as RootState
    const user = state.user.user
    return await boardsApi.updateSystemField(user.family_user_id, fieldID, data, signal)
  }
)

export const updateBoardFieldThunk = createAsyncThunk(
  'boards/updateBoardFieldThunk',
  async (
    {
      nodeId,
      fieldID,
      data,
      signal,
      fieldName,
    }: { nodeId: string, fieldID: string, data: any, signal?: AbortSignal, fieldName: 'status' | 'tags' | 'description' },
    { getState, dispatch }
  ) => {
    try{
      const state = getState() as RootState
      const user = state.user.user
      const rsp = await boardsApi.updateBoardFields(user.family_user_id, nodeId, fieldID, data, signal)
      const newValue = rsp.value

      switch (fieldName) {
      case 'status':
        dispatch(actions.updateBoardFieldsValues({ status: newValue ?? null }))
        break
      case 'tags':
        dispatch(actions.updateBoardFieldsValues({ tags: newValue ?? [] }))
        break
      case 'description':
        dispatch(actions.updateBoardFieldsValues({ description: newValue ?? '' }))
        break
      }
    } catch (e) {
      console.log('updateBoardFieldThunk error ', e)
    }
  }
)

export const deleteBoardFieldThunk = createAsyncThunk(
  'boards/deleteBoardFieldThunk',
  async ({ nodeId, fieldID, signal }: { nodeId: string, fieldID: string, signal: any }, { getState }) => {
    const state = getState() as RootState
    const user = state.user.user
    await boardsApi.deleteBoardFields(user.family_user_id, nodeId, fieldID, signal)
  }
)


export const changeBoardTagsThunk = createAsyncThunk(
  'boards/changeBoardTagsThunk',
  async ({ nodeId, tagsFieldID, data, signal }: { nodeId: string, tagsFieldID: string, data: any, signal: any }, { getState, dispatch }) => {
    const state = getState() as RootState
    const user = state.user.user
    const rsp = await boardsApi.updateBoardFields(user.family_user_id, nodeId, tagsFieldID, data, signal)
    const newValue = rsp.value
    dispatch(actions.updateBoardFieldsValues({ tags: newValue }))
  }
)

export const createNewSystemTagThunk = createAsyncThunk(
  'boards/createNewSystemTagThunk',
  async ({ fieldID, systemTags, boardTags, newTagValue, nodeId, signal }: {fieldID: string, systemTags: any, boardTags: any, newTagValue: any, nodeId: any, signal: any}, { getState, dispatch }) => {
    const state = getState() as RootState
    const data = { options: [...systemTags, { value: newTagValue }] }
    const rsp = await dispatch(updateSystemFieldThunk({ fieldID, data, signal })).unwrap()
    const { options } = rsp
    const newSysTag = options.find((tag) => tag.value === newTagValue)
    const newBoardTagsIds = [...boardTags, newSysTag.id ]
    await dispatch(changeBoardTagsThunk({ nodeId, tagsFieldID: fieldID, data: { value: newBoardTagsIds }, signal }))
    await dispatch(fetchSystemFieldsThunk({ signal }))
    const currentAssetsFields = state.assets.systemFields
    const updatedAssetsFields = (currentAssetsFields ?? []).map((f: any) =>
      f.title === 'tags' ? { ...f, options } : f
    )
    dispatch(assetsActions.setSystemFields(updatedAssetsFields))
  }
)

export const resetBoardsStateThunk = createAsyncThunk(
  "boards/resetBoardsStateThunk",
  async (_, { dispatch }) => {
    dispatch(actions.resetState())
  }
)

export const loadBoardAssetsInBackgroundThunk = createAsyncThunk(
  'boards/loadBoardAssetsInBackground',
  async ({ boardId, currentFileId, callback }: { boardId: string, currentFileId: string, callback: (items: any[], currentIndex: number) => void }) => {
    const sdk = getFilesSdk()
    const { familyFolderOperationsService: folderOperationsService } = sdk

    const nodesOptions = {
      embedded: true,
      preview_jwt: true,
      preview: true,
      type__in: ['link'],
      // trash_state: 'live'
    }

    const delay = 100
    const pageSize = 500
    const allNodes = await folderOperationsService.getAllChildNodes(
      boardId,
      nodesOptions,
      pageSize,
      delay
    )

    const filteredNodes = allNodes
      .filter((node: any) => !node.is_trashed)
      .filter((node: IFsNodeSchema) => node.type === NodeTypes.LINK)

    const currentIndex = filteredNodes.findIndex(node => node.id === currentFileId)

    if (currentIndex !== -1 && filteredNodes.length > 1) {
      callback(filteredNodes, currentIndex)
    }
  }
)

export const boardsSlice = createSlice({
  name: "boards",
  initialState: adapter.getInitialState(initialState),
  reducers: {
    setRenamingItem: (state, action) => {
      state.renamingItem = action.payload
    },
    setRenamingItemId: (state, action) => {
      state.renamingItemId = action.payload
    },
    setSelectedParentId: (state, action) => {
      state.selectedParentId = action.payload
    },
    setStatus: (state, action) => {
      state.status = action.payload
    },
    addItem: (state, action) => {
      adapter.removeOne(state, action.payload.id)
      adapter.addOne(state, action.payload)
    },
    updateItem: (state, action) => {
      adapter.updateOne(state, {
        id: action.payload.id,
        changes: action.payload,
      })
    },
    updateManyItems: (state, action) => {
      adapter.updateMany(state, action.payload)
    },
    setAll: (state, action) => {
      adapter.setAll(state, action.payload)
    },
    selectItem: selectFileListItemReducer(boardsSelectors),
    selectItemWithPressedCtrl: selectFileListItemWithPressedCtrlReducer(),
    selectItemWithPressedShift:
      selectFileListItemWithPressedShiftReducer(boardsSelectors),
    selectAll: selectAllFileListItemsReducer,
    unselectAll: unselectAllFileListItemsReducer,
    setSelectedItemsIds: (state, action: PayloadAction<string[]>) => {
      state.selectedItemsIds = action.payload
    },
    setCurrentBoard: (state, action) => {
      state.currentBoard = action.payload
    },
    setCurrentBoardId: (state, action) => {
      state.currentBoardId = action.payload
    },
    deleteItems: (state, action: PayloadAction<string[]>) => {
      adapter.removeMany(state, action.payload)
    },
    resetState: (state) => {
      adapter.removeAll(state)
      state.status = LOADING_STATUSES.IDLE
      state.error = null
      state.selectedItemsIds = []
      state.lastSelectedWithShiftIndex = null
      state.lastSelectedIndexes = []
      state.currentBoardId = ""
      state.currentBoard = null
    },
    setSystemFields: (state, action) => {
      state.systemFields = action.payload
    },
    setSystemFieldsValues: (state, action: PayloadAction<{ statusOptions: any[] |  null, tags: string[] | string | null }>) => {
      state.systemFieldsValues = action.payload
    },
    updateSystemFieldsValues: (state, action: PayloadAction<{ status?: string[] | string | null, tags?: string[] | string | null }>) => {
      state.systemFieldsValues = {
        ...state.systemFieldsValues,
        ...action.payload,
      }
    },
    setBoardFieldsValues: (state, action: PayloadAction<{ status: string[] | string | null, tags: string[] | string | null, description: string | null }>) => {
      state.boardFieldsValues = action.payload
    },
    updateBoardFieldsValues: (state, action: PayloadAction<{ status?: string[] | string | null, tags?: string[] | string | null, description?: string | null }>) => {
      state.boardFieldsValues = {
        ...state.boardFieldsValues,
        ...action.payload,
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchAllBoardsThunk.pending, (state) => {
      state.status = LOADING_STATUSES.LOADING
    })
      .addCase(fetchAllBoardsThunk.fulfilled, (state) => {
        state.status = LOADING_STATUSES.SUCCEEDED
      })
      .addCase(fetchAllBoardsThunk.rejected, (state) => {
        state.status = LOADING_STATUSES.FAILED
      })
  }

})

export default boardsSlice.reducer

const { reducer, actions } = boardsSlice

export { actions as boardsActions, reducer as boardsReducer }
